import { Button, LinkButton } from '@pmi/dsm-react';
import { CloseSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import TagsInput from 'react-tagsinput';
import wizardStyles from '../../../shared/components/InviteWizard/wizard.module.scss';
import { useSummarizeInviteesMutation } from '../../../shared/components/InviteWizard/apiSlice';
import { emailRegex } from '../../../shared/helpers';
import {
  AccessRuleItems,
  ExclusionItem,
} from '../../../shared/models/AccessRule';
import styles from './access-rules.module.scss';

// @ts-ignore css side-effect import
import 'react-tagsinput/react-tagsinput.css';

interface ExclusionsTabProps {
  rule: AccessRuleItems;
  dealId: string;
  onUploadingChange: (uploading: boolean) => void;
  mode: 'add' | 'remove';
  pendingAdds: ExclusionItem[];
  pendingRemovals: ExclusionItem[];
  onPendingAddsChange: (adds: ExclusionItem[]) => void;
  onPendingRemovalsChange: (removals: ExclusionItem[]) => void;
  onPendingChangesUpdate?: (hasChanges: boolean) => void;
  emailTags: string[];
  invalidEmailSet: Set<string>;
  onEmailTagsChange: (tags: string[]) => void;
  onInvalidEmailSetChange: (set: Set<string>) => void;
}

export default function ExclusionsTab({
  rule,
  dealId,
  onUploadingChange,
  mode,
  pendingAdds,
  pendingRemovals,
  onPendingAddsChange,
  onPendingRemovalsChange,
  onPendingChangesUpdate = undefined,
  emailTags,
  invalidEmailSet,
  onEmailTagsChange,
  onInvalidEmailSetChange,
}: ExclusionsTabProps) {
  const [summarizeInvitees, { isLoading: isUploading }] =
    useSummarizeInviteesMutation();
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const mountedRef = useRef(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const [uploadError, setUploadError] = useState('');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      onUploadingChange(false);
    };
  }, [onUploadingChange]);

  const {
    domainName,
    accountNumber,
    accountId,
    agreementId,
    providerNumber,
    createdByPersonId,
    id,
  } = rule;

  const errorEmailMessage =
    invalidEmailSet.size > 0
      ? 'The highlighted email(s) entered do not match the domain format and will not be added to the exclusion list'
      : '';

  const isEmailDomainValid = (email: string): boolean => {
    const emailDomain = email.split('@')[1]?.toLowerCase();
    return emailDomain === domainName?.toLowerCase();
  };

  useEffect(() => {
    const hasChanges =
      mode === 'add' ? pendingAdds.length > 0 : pendingRemovals.length > 0;
    onPendingChangesUpdate?.(hasChanges);
  }, [
    pendingAdds.length,
    pendingRemovals.length,
    mode,
    onPendingChangesUpdate,
  ]);

  const existingEmails = new Set(
    (rule.exclusions ?? []).map((x) => x.exclusionValue.trim().toLowerCase()),
  );
  const pendingEmails = new Set(
    pendingAdds.map((x) => x.exclusionValue.trim().toLowerCase()),
  );

  const toExclusion = (email: string): ExclusionItem => ({
    id: 0,
    accountNumber,
    accountId,
    agreementId,
    providerNumber,
    exclusionValue: email.trim(),
    createdByPersonId,
    reasonForExclusion: '',
    accessRuleId: id,
  });

  const downloadTemplate = () => {
    const content = `** Maximum of 1000 emails are supported\n"** Enter one email per cell in column A, starting directly below ""Email"""\n***************************************************\nEmail\n`;
    const url = URL.createObjectURL(
      new Blob([content], { type: 'text/csv;charset=utf-8;' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Agreement_UserInvite_Email_Template.csv';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      link.remove();
    }, 100);
  };

  const handleCsvUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || isUploading || !rule.isActive) return;
    setUploadMessage('');
    setUploadError('');
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setUploadError('Please upload a .csv file.');
      return;
    }
    onUploadingChange(true);
    try {
      // The shared parser requires text/csv; browsers may report another MIME type.
      const csvFile = new File([file], file.name, { type: 'text/csv' });
      const result = await summarizeInvitees({
        accountId: accountNumber,
        dealId,
        type: 'domainInvite',
        body: {
          dealUserRequestJson: JSON.stringify({ domains: [domainName] }),
          excludedEmailsCsvFile: csvFile,
        },
      }).unwrap();
      if (!mountedRef.current) return;
      const seen = new Set([...existingEmails, ...pendingEmails]);
      const accepted: string[] = [];
      const rejected = (result.invalidCsvExcludedEmails ?? []).map((email) =>
        email.trim(),
      );
      (result.validCsvExcludedEmails ?? []).forEach((rawEmail) => {
        const email = rawEmail.trim();
        if (!emailRegex.test(email) || !isEmailDomainValid(email)) {
          rejected.push(email);
        } else if (!seen.has(email.toLowerCase())) {
          seen.add(email.toLowerCase());
          accepted.push(email);
        }
      });
      onPendingAddsChange([...accepted.map(toExclusion), ...pendingAdds]);
      setUploadMessage(
        `${file.name}: ${accepted.length} new email(s) added. Save changes to update the exclusion list.`,
      );
      if (rejected.length > 0) {
        setUploadError(
          `${
            rejected.length
          } email(s) were not added because they are invalid or do not match the domain: ${rejected
            .slice(0, 10)
            .join(', ')}${rejected.length > 10 ? ', …' : ''}`,
        );
      }
    } catch {
      if (mountedRef.current) {
        setUploadError(
          `There was a problem with ${file.name}. Please review your file and upload it again.`,
        );
      }
    } finally {
      if (mountedRef.current) onUploadingChange(false);
    }
  };

  const handleTagsChange = (tags: string[]) => {
    const valid: string[] = [];
    const newInvalid = new Set<string>();
    tags.forEach((t) => {
      // Split by both comma and space
      const emailsToValidate = t
        .split(/[,\s]+/) // Split by comma or space
        .map((e) => e.trim())
        .filter((e) => e.length > 0);

      emailsToValidate.forEach((email) => {
        if (emailRegex.test(email)) {
          if (isEmailDomainValid(email)) {
            valid.push(email);
          } else {
            valid.push(email);
            newInvalid.add(email.toLowerCase());
          }
        }
      });
    });

    const cleaned = [
      ...new Set(
        valid.filter(
          (t) =>
            t.length > 0 &&
            !existingEmails.has(t.toLowerCase()) &&
            !pendingEmails.has(t.toLowerCase()),
        ),
      ),
    ];
    onEmailTagsChange(cleaned);
    onInvalidEmailSetChange(newInvalid);
  };

  const handleAddToPending = () => {
    const validTags = emailTags.filter(
      (email) => !invalidEmailSet.has(email.toLowerCase()),
    );
    const newExclusions = validTags.map(toExclusion);
    onPendingAddsChange([...newExclusions, ...pendingAdds]);
    const remainingInvalid = emailTags.filter((email) =>
      invalidEmailSet.has(email.toLowerCase()),
    );
    onEmailTagsChange(remainingInvalid);
  };

  const handleRemovePendingAdd = (email: string) => {
    onPendingAddsChange(pendingAdds.filter((x) => x.exclusionValue !== email));
  };

  const handlePasteSplit = (data: string) =>
    data
      .replaceAll(',', ' ')
      .replaceAll('\n', ' ')
      .split(' ')
      .map((d) => d.trim());

  const handleMarkForRemoval = (exclusion: ExclusionItem) => {
    onPendingRemovalsChange([exclusion, ...pendingRemovals]);
  };

  const handleUndoRemoval = (exclusion: ExclusionItem) => {
    onPendingRemovalsChange(
      pendingRemovals.filter((x) => x.id !== exclusion.id),
    );
  };

  const isActive = rule.isActive && !isUploading;
  const visibleExclusions = rule?.exclusions ?? [];

  const isMarkedForRemoval = (exclusion: ExclusionItem) =>
    pendingRemovals.some((x) => x.id === exclusion.id);

  const allEmails = [...pendingAdds, ...visibleExclusions];

  const shouldShowEmailContent =
    (mode === 'add' && allEmails.length > 0) ||
    (mode === 'remove' && visibleExclusions.length > 0);

  return (
    <div className={styles.excludedEmailContainer}>
      {mode === 'add' && (
        <>
          <div className="mb-2 d-flex align-items-center gap-1 small">
            <span>Use</span>
            <LinkButton
              titleText="this template"
              variant="light"
              size="xs"
              onClick={downloadTemplate}
              className={styles.smallLinkText}
            />
            <span>and</span>
            <LinkButton
              titleText="upload a .csv file."
              variant="light"
              size="xs"
              disabled={!isActive}
              onClick={() => uploadInputRef.current?.click()}
              className={styles.smallLinkText}
            />
            <input
              type="file"
              accept=".csv,text/csv"
              className="d-none"
              aria-label="Upload exclusion emails CSV"
              ref={uploadInputRef}
              onChange={handleCsvUpload}
              disabled={!isActive}
            />
          </div>
          {isUploading && <div role="status">Processing CSV…</div>}
          {uploadMessage && (
            <div role="status" className="mb-2">
              {uploadMessage}
            </div>
          )}
          {uploadError && (
            <div role="alert" className="text-danger mb-2">
              {uploadError}
            </div>
          )}
          <div className="d-block mb-3">
            <div className="d-flex flex-column item flex-md-row gap-2 align-items-md-center">
              <div className="flex-grow-1">
                <label htmlFor={`email-${id}`}>
                  Email addresses to exclude
                </label>
                <TagsInput
                  value={emailTags}
                  onChange={handleTagsChange}
                  addOnBlur
                  addKeys={[9, 13, 32, 188]}
                  pasteSplit={handlePasteSplit}
                  onlyUnique
                  validationRegex={emailRegex}
                  disabled={!isActive}
                  inputProps={{
                    placeholder: 'input@email.com',
                    id: `email-${id}`,
                    style:
                      emailTags.length === 0 ? { width: '100%' } : undefined,
                  }}
                  renderTag={({
                    tag,
                    key: tagKey,
                    onRemove,
                    getTagDisplayValue,
                  }) => {
                    const isInvalid = invalidEmailSet.has(tag.toLowerCase());
                    return (
                      <span
                        key={tagKey}
                        className={`react-tagsinput-tag${
                          isInvalid ? ` ${styles.invalidTag}` : ''
                        }`}
                      >
                        {getTagDisplayValue(tag)}
                        <button
                          type="button"
                          aria-label={`Remove ${tag}`}
                          className="react-tagsinput-remove"
                          onClick={() => onRemove(tagKey as number)}
                        />
                      </span>
                    );
                  }}
                  className={`rounded-1 border border-1 px-2 py-2 ${
                    wizardStyles.tagsInput
                  } ${emailTags.length > 0 ? wizardStyles.hasInputTags : ''} ${
                    invalidEmailSet.size > 0 ? styles.invalidInput : ''
                  }`}
                />
                <p className="text-muted small mb-1">
                  These users will not be able to access your Partner Platform.
                  Include multiple, comma-separated emails to block more than
                  one person.
                </p>
              </div>
              <div className="d-flex d-md-contents gap-2 mb-3">
                <div className="flex-fill d-md-block">
                  <Button
                    titleText="Add"
                    variant="primary"
                    size="sm"
                    style={{
                      minWidth: 80,
                      width: '100%',
                      ...(!isActive ||
                      emailTags.filter(
                        (email) => !invalidEmailSet.has(email.toLowerCase()),
                      ).length === 0
                        ? { border: '1px solid #d9d9d9' }
                        : {}),
                    }}
                    disabled={
                      !isActive ||
                      emailTags.filter(
                        (email) => !invalidEmailSet.has(email.toLowerCase()),
                      ).length === 0
                    }
                    onClick={handleAddToPending}
                  />
                </div>
                <div className="flex-fill d-md-block">
                  <Button
                    titleText="Clear"
                    variant="outline-secondary"
                    size="sm"
                    style={{ minWidth: 80, width: '100%' }}
                    onClick={() => {
                      onEmailTagsChange([]);
                      onInvalidEmailSetChange(new Set());
                    }}
                    disabled={isUploading}
                  />
                </div>
              </div>
            </div>
          </div>
          {errorEmailMessage && (
            <div
              style={{
                color: '#dc3545',
                fontSize: '12px',
                marginTop: '-8px',
                marginBottom: '12px',
              }}
            >
              {errorEmailMessage}
            </div>
          )}
        </>
      )}

      {shouldShowEmailContent ? (
        <div>
          <div className={`${styles.emailListHeader} mb-1`}>
            Excluded emails:
          </div>
          <div className={styles.emailListContent}>
            {mode === 'add' && (
              <>
                {pendingAdds.map((x) => (
                  <div key={`pending-${x.exclusionValue}`}>
                    <hr />
                    <div
                      className={`${styles.emailListContainer} d-flex justify-content-between align-items-center`}
                    >
                      <div className={styles.emailListItem}>
                        {x.exclusionValue}
                        <span className="text-success ms-2">(new)</span>
                      </div>
                      <CloseSolidIcon
                        onClick={() =>
                          isActive && handleRemovePendingAdd(x.exclusionValue)
                        }
                        style={{
                          cursor: isActive ? 'pointer' : 'not-allowed',
                          opacity: isActive ? 1 : 0.5,
                        }}
                      />
                    </div>
                  </div>
                ))}
                {visibleExclusions.map((x) => (
                  <div key={x.id}>
                    <hr />
                    <div
                      className={`${styles.emailListContainer} d-flex justify-content-between align-items-center`}
                    >
                      <div className={styles.emailListItem}>
                        {x.exclusionValue}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
            {mode === 'remove' &&
              visibleExclusions.map((x) => (
                <div key={x.id}>
                  <hr />
                  <div
                    className={`${styles.emailListContainer} d-flex justify-content-between align-items-center`}
                    style={{
                      opacity: isMarkedForRemoval(x) ? 0.5 : 1,
                    }}
                  >
                    <div
                      className={styles.emailListItem}
                      style={{
                        textDecoration: isMarkedForRemoval(x)
                          ? 'line-through'
                          : 'none',
                      }}
                    >
                      {x.exclusionValue}
                    </div>
                    {isMarkedForRemoval(x) ? (
                      <button
                        onClick={() => isActive && handleUndoRemoval(x)}
                        disabled={!isActive}
                        type="button"
                        className="typography12 gray600 text-decoration-underline cursor-pointer"
                        id="btn-cart-undo-change"
                      >
                        Undo
                      </button>
                    ) : (
                      <CloseSolidIcon
                        onClick={() => isActive && handleMarkForRemoval(x)}
                        style={{
                          cursor: isActive ? 'pointer' : 'not-allowed',
                          opacity: isActive ? 1 : 0.5,
                        }}
                      />
                    )}
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className={`${styles.emailListHeader}`}>No Excluded emails</div>
      )}
    </div>
  );
}
